import './DataCard.scss'
import React, { ReactNode } from 'react'
import { Tooltip } from '@mui/material'
import AccentArea from 'components/AccentArea'
import { InfoCircleOutlinedIcon, XIcon, CheckMarkIcon } from 'components'
import { amountValueFormatter } from 'views/ChargebacksAGGrid/grids/ChargebacksGrid'

interface Timeframe {
    label: string
    percentage: string
    trend: string
}

interface DataCardProps {
    readonly type: string
    readonly title?: string
    readonly value: string | ReactNode
    readonly timeframe?: Timeframe
    readonly handleDataCardClick: any
    readonly isActive?: boolean
    readonly displayType?: 'stats' | 'trend'
    readonly stats?: {
        wins: string | null
        loss: string | null
        totalValue: string | null
    }
    readonly tooltipText?: string | null
}

export default function DataCard({
    type,
    title,
    value,
    timeframe = { label: 'vs last mo', percentage: '', trend: '' },
    handleDataCardClick,
    isActive = false,
    displayType,
    stats,
    tooltipText = null,
}: DataCardProps) {
    const IncreaseIcon = () => (
        <div data-svg-wrapper style={{ position: 'relative' }}>
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M14.6667 4.66664L9.42095 9.9124C9.15694 10.1764 9.02494 10.3084 8.87272 10.3579C8.73882 10.4014 8.59459 10.4014 8.4607 10.3579C8.30848 10.3084 8.17647 10.1764 7.91246 9.91239L6.08762 8.08755C5.82361 7.82354 5.6916 7.69154 5.53939 7.64208C5.40549 7.59857 5.26126 7.59857 5.12736 7.64208C4.97514 7.69154 4.84314 7.82354 4.57913 8.08756L1.33337 11.3333M14.6667 4.66664H10M14.6667 4.66664V9.33331"
                    stroke="#0D8050"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </div>
    )

    const DecreaseIcon = () => (
        <div data-svg-wrapper style={{ position: 'relative' }}>
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M14.6666 11.3333L9.42089 6.08755C9.15688 5.82354 9.02488 5.69154 8.87266 5.64208C8.73876 5.59857 8.59453 5.59857 8.46064 5.64208C8.30842 5.69154 8.17641 5.82354 7.9124 6.08756L6.08756 7.9124C5.82355 8.17641 5.69154 8.30841 5.53932 8.35787C5.40543 8.40138 5.2612 8.40138 5.1273 8.35787C4.97508 8.30841 4.84308 8.17641 4.57907 7.91239L1.33331 4.66664M14.6666 11.3333H9.99998M14.6666 11.3333V6.66664"
                    stroke="#DB3737"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </div>
    )

    return (
        <AccentArea
            hideHeader
            className={`c-case-management-card`}
            enableActiveUI={isActive}
            enableHover
        >
            <div
                role="presentation"
                key={type}
                id={`data-card-${type}`}
                onClick={() => {
                    handleDataCardClick(type)
                }}
            >
                <div className="c-case-management-card__inner">
                    <div className="c-case-management-card__title">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <span className="c-case-management-card__label">
                                {title ?? type}
                            </span>
                            {tooltipText && (
                                <Tooltip
                                    title={tooltipText}
                                    placement="top"
                                    arrow
                                >
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <InfoCircleOutlinedIcon className="c-case-management-card__circleInfo" />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                        {/* <FontAwesomeIcon
                            icon={faEllipsisH}
                            className="c-case-management-card__ellipsis"
                        /> */}
                    </div>
                    <div className="c-case-management-card__content">
                        <span className="c-case-management-card__value">
                            {value}
                        </span>
                        {displayType === 'trend' ? (
                            <div className="c-case-management-card__stats">
                                <div
                                    className={`c-case-management-card__percentage ${
                                        timeframe.trend === 'increase'
                                            ? '--increase'
                                            : '--decrease'
                                    }`}
                                >
                                    {timeframe.trend === 'increase' ? (
                                        <IncreaseIcon />
                                    ) : (
                                        <DecreaseIcon />
                                    )}
                                    {timeframe.percentage}
                                </div>
                                <div className="c-case-management-card__date">
                                    {timeframe.label}
                                </div>
                            </div>
                        ) : displayType === 'stats' &&
                          (stats?.loss || stats?.wins || stats?.totalValue) ? (
                            stats?.totalValue ? (
                                <div className="c-case-management-card__stats">
                                    <div
                                        className={`c-case-management-card__percentage ${
                                            Number(stats?.totalValue) >= 0
                                                ? '--increase'
                                                : '--decrease'
                                        }`}
                                    >
                                        {Number(stats?.totalValue) >= 0 ? (
                                            <CheckMarkIcon iconColor="#0d8050" />
                                        ) : (
                                            <XIcon iconColor="#db3737" />
                                        )}
                                        $
                                        {amountValueFormatter(
                                            stats?.totalValue.replace('-', '')
                                        )}
                                    </div>
                                    <div className="c-case-management-card__date">
                                        Total Value
                                    </div>
                                </div>
                            ) : (
                                <div className="c-case-management-card__stats">
                                    <div
                                        className={`c-case-management-card__percentage ${'--increase'}`}
                                    >
                                        <CheckMarkIcon iconColor="#0d8050" />
                                        <span>{stats?.wins}</span>
                                        <div className="c-case-management-card__winLoss">
                                            Wins
                                        </div>
                                    </div>
                                    <div
                                        className={`c-case-management-card__percentage ${'--decrease'}`}
                                        style={{ marginTop: '4px' }}
                                    >
                                        <XIcon iconColor="#db3737" />
                                        <span>{stats?.loss}</span>
                                        <div className="c-case-management-card__winLoss">
                                            Loss
                                        </div>
                                    </div>
                                </div>
                            )
                        ) : null}
                    </div>
                </div>
            </div>
        </AccentArea>
    )
}
